import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Printer } from 'lucide-react';

function OrderPackingSlip() {
  const { shop, id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`/admin/orders/${shop}/${id}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
        toast.error('Failed to load order');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [shop, id]);

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading&hellip;</div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Order not found.</div>
    );
  }

  const addr = order.shipping_address?.address;

  return (
    <div style={{ background: '#f4f4f4', minHeight: '100vh', padding: '30px 20px' }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="no-print" style={{ maxWidth: '640px', margin: '0 auto 16px', display: 'flex', justifyContent: 'space-between' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/admin/orders')}>
          <ArrowLeft size={16} /> Back to Orders
        </button>
        <button className="btn btn-primary" onClick={() => window.print()}>
          <Printer size={16} /> Print
        </button>
      </div>

      <div style={{
        maxWidth: '640px', margin: '0 auto', background: '#fff', borderRadius: '8px',
        border: '1px solid #e0e0e0', padding: '40px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #1a1a1a', paddingBottom: '20px', marginBottom: '24px' }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22px', fontWeight: '700' }}>the OBX Beach Bum</div>
            <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>Packing Slip</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '15px', fontWeight: '700' }}>Order #{order.order_number}</div>
            <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>{formatDate(order.created_at)}</div>
            <div style={{ fontSize: '13px', color: '#888' }}>{order.shop_label}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '40px', marginBottom: '28px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Customer</div>
            <div style={{ fontSize: '14px', fontWeight: '600' }}>{order.customer_name || '—'}</div>
            {order.customer_email && (
              <div style={{ fontSize: '13px', color: '#666' }}>{order.customer_email}</div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Ship To</div>
            {addr ? (
              <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                <div style={{ fontWeight: '600' }}>{order.shipping_address.name}</div>
                <div>{addr.line1}</div>
                {addr.line2 && <div>{addr.line2}</div>}
                <div>{addr.city}, {addr.state} {addr.postal_code}</div>
              </div>
            ) : (
              <div style={{ fontSize: '14px', color: '#888' }}>No shipping address on file</div>
            )}
          </div>
        </div>

        {order.order_notes && (
          <div style={{
            marginBottom: '20px', padding: '12px 14px', background: '#fef3c7',
            borderLeft: '4px solid #f59e0b', borderRadius: '4px', fontSize: '14px', lineHeight: '1.5',
            whiteSpace: 'pre-wrap'
          }}>
            <strong>Customer note:</strong> {order.order_notes}
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd' }}>
              <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
              <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Item</th>
              <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Qty</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 0', width: '60px' }}>
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt=""
                      style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', display: 'block' }}
                    />
                  )}
                </td>
                <td style={{ padding: '12px 12px 12px 0' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{item.title}</div>
                  {item.variant_label && (
                    <div style={{ fontSize: '13px', color: '#666' }}>{item.variant_label}</div>
                  )}
                  {item.special_instructions && (
                    <div style={{ fontSize: '13px', color: '#b45309', marginTop: '4px' }}>Note: {item.special_instructions}</div>
                  )}
                </td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontSize: '14px' }}>{item.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {order.billing ? (
          <div style={{ marginTop: '20px', marginLeft: 'auto', maxWidth: '260px', fontSize: '14px' }}>
            {order.billing.line_items.map((li, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#555' }}>
                <span>{li.description}{li.quantity > 1 ? ` x${li.quantity}` : ''}</span>
                <span>${li.amount_total.toFixed(2)}</span>
              </div>
            ))}
            {order.billing.discount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#555' }}>
                <span>Discount</span>
                <span>-${order.billing.discount.toFixed(2)}</span>
              </div>
            )}
            {order.billing.tax > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#555' }}>
                <span>Tax</span>
                <span>${order.billing.tax.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', marginTop: '4px', borderTop: '1px solid #ddd', fontSize: '16px', fontWeight: '700' }}>
              <span>Total</span>
              <span>${order.billing.total.toFixed(2)}</span>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: '20px', textAlign: 'right', fontSize: '16px', fontWeight: '700' }}>
            Total: ${order.total?.toFixed(2)}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderPackingSlip;
